import React, { useCallback, Suspense, memo } from 'react';
import { log } from 'log';
import { equals } from 'ramda';
import parse, { domToReact } from 'html-react-parser';

import { Link } from 'js/components/common/Link';
import { AccessibilityElement } from 'js/components/common/AccessibilityElement';

import { dispatcher } from 'dispatcher';
import { parseHTMLAttributes } from '@interlayer/js/app/utils/common.util';
import { useMountEffect } from 'js/hooks/useMountEffect';
import { componentService } from 'js/services/component.service';
import { useSmartClosePopups } from 'js/components/common/PopupManager';
import { parseStyleAttributes } from '@interlayer/js/components/system/WebContent/buzz/helpers';
import { BackboneReplacer, getBackboneReplacerViews } from 'js/backbone/components/BackboneReplacer';

import * as helpers from 'js/components/system/WebContent/components/WebContentInner/helpers';

/* core, added parseCamelCase(line 73) */
export const WebContentInnerBare = ({ id, content, popupId, className = '' }) => {
    const scripts = [];
    const replacers = getBackboneReplacerViews();
    const components = componentService.getWebContentComponents();
    const closePopups = useSmartClosePopups();
    const onClick = useCallback((event) => {
        const { target: { target } } = event;
        const isInTheSameWindow = target === '_self';

        if (popupId && isInTheSameWindow) {
            closePopups(popupId);
        }
    }, [popupId, closePopups]);
    const options = {
        replace: ({ attribs = {}, type, name, children }) => {
            if (type === 'script') {
                const script = document.createElement('script');

                script.appendChild(document.createTextNode(domToReact(children).toString()));

                if (attribs.src) {
                    script.src = attribs.src;
                }

                if (attribs.type) {
                    script.type = attribs.type;
                }

                scripts.push(script);
                return;
            }

            if (name === 'a') {
                // wrap all the links into Link element
                // so that regular routing can happen
                return <Link
                    onClick={onClick}
                    isDefaultLinkClass={false}
                    {...helpers.alignAttributesWithReact(attribs)}
                >
                    {domToReact(children, options)}
                </Link>;
            }

            if (name === 'head' || name === 'body') {
                // do not support <head> and <body> dom elements, show their children instead
                return <>{domToReact(children, options)}</>;
            }

            if (/^hub2-/.test(name)) {
                // name of the tag does stats with "hub2-"
                const componentName = name?.replace(/^hub2-|-/g, '');

                if (components[componentName]) {
                    const Component = components[componentName];

                    return (
                        <Suspense fallback="">
                            {children.length ? (
                                <Component {...parseStyleAttributes(parseHTMLAttributes(attribs))}>
                                    {domToReact(children, options)}
                                </Component>
                            ) : (
                                <Component {...parseStyleAttributes(parseHTMLAttributes(attribs))} />
                            )}
                        </Suspense>
                    );
                }

                // this is here only while we have Backbone replacers
                // it should be removed once all replacers are migrated to be real React components
                if (replacers[componentName]) {
                    return (
                        <BackboneReplacer dynamicName={componentName} viewName={componentName} {...attribs}>
                            {domToReact(children, options)}
                        </BackboneReplacer>
                    );
                }
            }

            if (attribs.class?.includes('fn-redirect') && (attribs['data-href'] || attribs['data-url']) && !attribs.tabindex) {
                attribs.tabindex = 0;
            }

            if (type === 'tag') {
                const props = helpers.alignAttributesWithReact(attribs);
                const childrenContent = children?.length ? domToReact(children, options) : null;

                if (Number(attribs.tabindex) === 0) {
                    return (
                        <AccessibilityElement tagName={name} {...props}>
                            {childrenContent}
                        </AccessibilityElement>
                    );
                }

                return React.createElement(
                    name,
                    props,
                    childrenContent
                );
            }
        }
    };
    const dataAttrs = {};

    if (id) {
        dataAttrs['data-web-content-id'] = id;
    }

    useMountEffect((isMounted) => {
        if (isMounted()) {
            for (const script of scripts) {
                document.body.appendChild(script);
            }

            if (id) {
                dispatcher.dispatch('webContent:rendered', id);
            }

            return () => {
                for (const script of scripts) {
                    try {
                        document.body.removeChild(script);
                    } catch (e) {
                        log('error', `Unable to remove script from webcontent ${id}`, e);
                    }
                }
            };
        }
    }, [id, scripts]);

    return (
        <div
            {...dataAttrs}
            className={`${className}`}
        >
            <article>{parse(content.toString(), options)}</article>
        </div>
    );
};

export const WebContentInner = memo(WebContentInnerBare, equals);
